import React, { useState } from "react";
import { get, post, del } from "../api";
import { errText, getLang, t } from "../i18n";
import { toast, useStore } from "../store";
import { Avatar, Button, Empty, GiftIcon, Link, Spinner, useAsync } from "../ui";
import { startCall } from "../calls";
import { GiftModal, ReportModal } from "./room";

export function Profile({ username }: { username: string }) {
  const me = useStore((s) => s.me)!;
  const p = useAsync(() => get(`/api/users/${encodeURIComponent(username)}`), [username]);
  const isMe = p.data?.user?.id === me.id;
  const history = useAsync(() => (isMe ? get("/api/me/matches") : Promise.resolve({ matches: [] })), [isMe]);
  const [gift, setGift] = useState(false);
  const [report, setReport] = useState(false);
  if (p.loading && !p.data) return <Spinner />;
  if (p.error) return <Empty>{errText(p.error)}</Empty>;
  const { user, stats, achievements, gifts, relation, perGame } = p.data;
  const frame = (p.data.equipped ?? []).find((i: any) => i.category === "frame")?.icon?.split(":")[1];
  const act = async (fn: () => Promise<any>) => {
    try {
      await fn();
      p.reload();
    } catch (e) {
      toast(errText(e), "error");
    }
  };
  const winRate = stats?.games ? Math.round((stats.wins / stats.games) * 100) : 0;
  const pct = Math.min(100, ((user.xp - (user.level - 1) ** 2 * 100) / (user.nextLevelXp - (user.level - 1) ** 2 * 100)) * 100);

  return (
    <div className="profile">
      <header className="profile-head">
        <Avatar name={user.displayName} url={user.avatarUrl} size={96} online={user.online} frame={frame} />
        <div className="grow">
          <h1>{user.displayName}</h1>
          <p className="muted" dir="ltr">@{user.username}</p>
          {user.bio && <p>{user.bio}</p>}
          <div className="xpbar" title={`${user.xp} XP`}><span style={{ width: `${pct}%` }} /></div>
          <small className="muted">{t.level} {user.level}</small>
        </div>
        {!isMe && relation && (
          <div className="profile-actions">
            {relation.friend === "none" && <Button onClick={() => act(() => post("/api/friends/request", { userId: user.id }))}>{t.addFriend}</Button>}
            {relation.friend === "outgoing" && <Button kind="ghost" disabled>{t.requestSent}</Button>}
            {relation.friend === "incoming" && <Button onClick={() => act(() => post("/api/friends/respond", { userId: user.id, accept: true }))}>{t.accept}</Button>}
            {relation.friend === "friends" && <Button kind="ghost" onClick={() => act(() => del(`/api/friends/${user.id}`))}>{t.remove}</Button>}
            {relation.friend === "friends" && !relation.blockedByMe && !relation.blockedMe && (
              <>
                <Link to={`/messages/${user.username}`} className="btn btn-ghost">{t.messages}</Link>
                <Button kind="ghost" onClick={() => void startCall({ id: user.id, username: user.username, displayName: user.displayName, avatarUrl: user.avatarUrl }).then((e) => e && toast(errText({ code: e }), "error"))}>{t.messageCall}</Button>
              </>
            )}
            <Button kind="ghost" onClick={() => setGift(true)}>{t.sendGift}</Button>
            <Button kind="quiet" onClick={() => setReport(true)}>{t.report}</Button>
            {relation.blockedByMe ? (
              <Button kind="quiet" onClick={() => act(() => del(`/api/blocks/${user.id}`))}>{t.unblock}</Button>
            ) : (
              <Button kind="danger" onClick={() => act(() => post("/api/blocks", { userId: user.id }))}>{t.block}</Button>
            )}
          </div>
        )}
        {isMe && <Link to="/settings" className="btn btn-ghost">{t.settings}</Link>}
      </header>

      {stats && (
        <section className="panel stats">
          <div><b>{stats.games}</b><span>{t.matches}</span></div>
          <div><b>{stats.wins}</b><span>{t.wins}</span></div>
          <div><b>{stats.losses}</b><span>{t.losses}</span></div>
          <div><b>{winRate}%</b><span>{t.winRate}</span></div>
        </section>
      )}
      {perGame?.length > 0 && (
        <section className="panel">
          <h2>{t.games}</h2>
          <ul className="list">
            {perGame.map((g: any) => (
              <li key={g.game_id} className="list-row"><span className="grow">{t.game[g.game_id]}</span><span>{g.wins}/{g.games}</span></li>
            ))}
          </ul>
        </section>
      )}
      <section className="panel">
        <h2>{t.achievements}</h2>
        {achievements.length ? (
          <ul className="achievements">
            {achievements.map((a: any) => (
              <li key={a.id}><span className="ach-mark" aria-hidden="true">★</span>{getLang() === "ar" ? a.name_ar : a.name_en}</li>
            ))}
          </ul>
        ) : (
          <Empty>{t.noAchievements}</Empty>
        )}
      </section>
      {gifts.length > 0 && (
        <section className="panel">
          <h2>{t.giftsReceived}</h2>
          <ul className="gift-shelf">
            {gifts.map((g: any) => (
              <li key={g.sku}><GiftIcon icon={g.icon} size={40} /><small>×{g.n}</small></li>
            ))}
          </ul>
        </section>
      )}
      {isMe && (
        <section className="panel">
          <h2>{t.matchHistory}</h2>
          {history.data?.matches?.length ? (
            <ul className="list">
              {history.data.matches.map((m: any) => (
                <li key={m.id} className="list-row">
                  <span className={m.won ? "win-dot" : "loss-dot"} aria-hidden="true" />
                  <span className="grow">{t.game[m.game_id]} · {m.players.map((p: any) => p.displayName).join("، ")}</span>
                  <span>{t.place(m.place)}</span>
                  <small className="muted">{new Date(m.ended_at).toLocaleDateString(getLang() === "ar" ? "ar-SA" : "en")}</small>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>{t.noMatches}</Empty>
          )}
        </section>
      )}
      {gift && <GiftModal to={{ id: user.id, name: user.displayName }} onClose={() => setGift(false)} />}
      {report && <ReportModal userId={user.id} name={user.displayName} onClose={() => setReport(false)} />}
    </div>
  );
}
